import { useSearchParams } from 'react-router-dom'
import type { Facet } from '../lib/entries.ts'

/**
 * The facet rail above a department listing. Filters live in the URL so a
 * filtered view can be linked to and shared.
 *
 * It renders only the facets it is handed — the caller builds those from the
 * catalog (see buildFacets), so every chip here leads somewhere. Each carries
 * its result count, which is what turns the row from a guess into a decision.
 */
export function FilterBar({ qa, type }: { qa: Facet[]; type: Facet[] }) {
  const [params, setParams] = useSearchParams()

  const active = (key: string) => params.get(key)
  const isFiltered = Boolean(active('qa') || active('type'))

  const toggle = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (next.get(key) === value) next.delete(key)
    else next.set(key, value)
    setParams(next, { replace: true })
  }

  const clear = () => {
    const next = new URLSearchParams(params)
    next.delete('qa')
    next.delete('type')
    setParams(next, { replace: true })
  }

  const group = (key: string, label: string, facets: Facet[]) => {
    if (facets.length === 0) return null
    return (
      <div className="facets__group">
        <p className="facets__label" id={`facet-${key}`}>{label}</p>
        <div className="facets__chips" role="group" aria-labelledby={`facet-${key}`}>
          {facets.map((f) => (
            <button
              key={f.value}
              type="button"
              aria-pressed={active(key) === f.value}
              // Spelled out, because the label and the count sit in adjacent
              // elements: the name derived from the DOM would run them
              // together as "QA21", which a screen reader reads as "QA
              // twenty-one".
              aria-label={`${f.label}, ${f.count} ${f.count === 1 ? 'item' : 'items'}`}
              className={active(key) === f.value ? 'chip is-active' : 'chip'}
              onClick={() => toggle(key, f.value)}
            >
              {f.label}
              <span className="chip__n">{f.count}</span>
            </button>
          ))}
        </div>
      </div>
    )
  }

  if (qa.length === 0 && type.length === 0) return null

  return (
    <div className="facets">
      {group('qa', 'Quality area', qa)}
      {group('type', 'Document type', type)}
      {isFiltered && (
        <button type="button" className="facets__clear" onClick={clear}>
          Clear filters
        </button>
      )}
    </div>
  )
}
